/**
 * The inside of a bookmark-list window: a filter header, the rows, a footer strip.
 *
 * Everything that *decides* anything is in
 * [se.soderbjorn.lunapin.client.viewmodel.BookmarkListBackingViewModel] and in
 * `BookmarkFilter.kt` beside it — what the filter keeps, what the rail's counts are
 * counted against, what the window is called. This file paints one state and reports
 * gestures back, exactly as `EditBookmarkView` does for the editor, so an Android or iOS
 * list renders the same window from the same flow rather than rediscovering the rules.
 *
 * ── Three bands, and why the middle one is the only thing that scrolls ───────
 *
 * The header (search, tag rail, knobs) and the footer (counts, keys) stay put; the rows
 * scroll between them. That is what makes **expanding a row in place** free: the scrolling
 * element is not rebuilt, so a repaint that replaces every row leaves `scrollTop` exactly
 * where it was. There is no preview pane and there is not going to be one — reading a
 * summary must not cost the reader their position in the list, and a pane would take the
 * window's width away from the thing the window is for.
 *
 * ── Rebuilt wholesale, except the two things with a caret in them ────────────
 *
 * The rows, the rail and the footer are torn down and rebuilt on every state, for the
 * reason the rest of `:web` does it: a render that is a pure function of the state has no
 * path where a stale node survives because a branch forgot to clear it.
 *
 * The search field is the exception, and it is the same exception the editor has: it holds
 * a caret, a selection and a half-typed `#tag` with a menu under it. So it is built once,
 * as [TagInputField] — **LPN-14's control, not a second implementation of it** — and told
 * what is committed. Its committed set is the window's include and exclude tags, which is
 * exactly the seam that control was written with: the tag panel and the field are two views
 * of one filter, and removing a chip in either removes it in both because neither of them
 * owns it.
 *
 * The tag panel's *Find a tag…* is the second, and it was found the hard way: it held a
 * caret and was rebuilt anyway, so every character typed into it blurred the field it had
 * just been typed into and only the first one landed (LPN-41). It is a member now, like the
 * search field, and the panel around it is repainted rather than replaced. The rule the two
 * of them make is worth stating plainly, because the next field on this surface will meet
 * it too: **anything that can hold a caret is built once; everything else is rebuilt.**
 *
 * ── The header is one row, and that is LPN-66 ────────────────────────────────
 *
 * It was three bands — a search row, a tag rail clipped to two rows of chips, and a wrapping
 * strip of six selects — and it ran from about 160 pixels to over 460 depending on what was
 * applied and whether the rail was open. Everything that is not the condition itself now lives
 * behind two buttons that raise floating panels, so the bar's height does not change with the
 * filter and a pane's worth of window goes back to the rows. [buildHeader] lists what moved
 * where and why.
 *
 * ── What the canvas draws that is deliberately not here ─────────────────────
 *
 *  - **`⌘K COMMANDS` and `T TAG`** in the footer's key legend. There is no command palette
 *    and no inline tag action. Drawing a key that does nothing is worse than drawing no
 *    key at all.
 *  - **A per-domain colour** on the favicon tile. Every colour on this surface is a lunula
 *    `--t-*` token so the window follows all 84 themes; a generated hue would be the one
 *    thing on it that does not, and the tile's job — telling two domains apart at a glance
 *    — is done by the initials.
 *  - **A third row action** in the expanded row's hover cluster. Open and edit are the two
 *    that exist; an overflow button with nothing behind it is furniture.
 *
 * ── One row shape, at every width ────────────────────────────────────────────
 *
 * There was a `ROWS` knob offering *Summaries* and *Compact*, and it is gone; so is the
 * one-line row behind it. The whole of what this window promises is that **you can tell
 * what a bookmark is without opening it**, and a one-line row could not keep that promise
 * at any width — the title shared the line with the domain, the time, the chips and *Edit*,
 * so it was ellipsised at thirty characters while the row was five hundred pixels wide.
 *
 * What is left is the card, and it stacks the same way whether it has a column to itself or
 * is one of three:
 *
 *  - **the title takes the whole width**, wrapping, up to four lines before it gives up.
 *    Nothing shares its line. This is the single change that made the ellipsis avoidable:
 *    the metadata used to sit beside it and take a third of the row to say `share.google
 *    10:50`.
 *  - **the metadata is the line under it** — domain, time, *Edit* — and the domain is
 *    dropped when the title *is* the URL, which is the one case it was printed twice.
 *  - **the tags wrap under that**, all of them. No cap and no *More*: those were a one-line
 *    row's apparatus for clipping, and clipping is what they did badly.
 *  - **both prose blocks show**, the owner's note first and the model's summary under it,
 *    two lines each, with *Show more* where a clamp actually bit.
 *
 * ── What a row says while it waits, and after it gives up ────────────────────
 *
 * Three states of one row, and the rule underneath all three is that **a bookmark whose
 * page could not be read is still a perfectly good bookmark**: its title, domain, tags and
 * the owner's own note render exactly as they always do. What changes is only the slot the
 * generated summary lives in.
 *
 *  - **waiting** (`pending`/`running`) — a `SUMMARISING` badge takes the time's place at
 *    the row's right edge, mono and outlined in the warning colour rather than the accent,
 *    because it is a state and not a selection. Where the summary will land, two skeleton
 *    bars. **Not a spinner**: the row is not blocked, it is incomplete, and a skeleton is
 *    the shape that says which. The `AI` pill is absent — there is nothing yet to
 *    attribute. The note above it is untouched, which is the point of showing both: a row
 *    waiting for a summary no longer hides the sentence its owner wrote.
 *  - **failed** — one quiet line where the summary would be, the server's own sentence on
 *    hover, and *Try again*. A note on a row rather than an error state for the row.
 *  - **anything else** — as before.
 *
 * The window asks the server again every few seconds while any row on screen is waiting,
 * and stops when none is. That timer is in the view model, not here: "is any row in the
 * *current result set* waiting" is a question only the thing holding the collection and the
 * filter can answer, and a timer built in the DOM would outlive the window that opened it.
 *
 * @see se.soderbjorn.lunapin.client.viewmodel.BookmarkListBackingViewModel
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.events.MouseEvent
import kotlin.js.Date
import kotlin.math.abs
import se.soderbjorn.lunapin.client.viewmodel.AddedWithins
import se.soderbjorn.lunapin.client.viewmodel.AiSummaryFilters
import se.soderbjorn.lunapin.client.viewmodel.AiTagFilters
import se.soderbjorn.lunapin.client.viewmodel.BookmarkColumns
import se.soderbjorn.lunapin.client.viewmodel.BookmarkDayGroup
import se.soderbjorn.lunapin.client.viewmodel.BookmarkGroupings
import se.soderbjorn.lunapin.client.viewmodel.BookmarkListBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.BookmarkListBackingViewModel.State
import se.soderbjorn.lunapin.client.viewmodel.BookmarkListBackingViewModel.TagChipState
import se.soderbjorn.lunapin.client.viewmodel.BookmarkSorts
import se.soderbjorn.lunapin.client.viewmodel.FilterMatches
import se.soderbjorn.lunapin.client.viewmodel.RAIL_PAGE
import se.soderbjorn.lunapin.client.viewmodel.RailSorts
import se.soderbjorn.lunapin.client.viewmodel.RailTag
import se.soderbjorn.lunapin.client.viewmodel.WindowContent
import se.soderbjorn.lunapin.client.viewmodel.isAwaitingSummary
import se.soderbjorn.lunapin.clientserver.AiStates
import se.soderbjorn.lunapin.clientserver.Bookmark
import se.soderbjorn.lunula.web.shell.buildMenuTrigger
import se.soderbjorn.lunula.web.shell.setMenuTriggerLabel
import se.soderbjorn.lunula.web.shell.setMenuTriggerOpen

/**
 * One list window's DOM.
 *
 * @param model the window's backing view model. Every gesture goes to it; nothing is
 *   decided here.
 * @param onEditBookmark open a bookmark in an edit window — `e`, and the row's own edit
 *   action. Whether that makes a window or focuses one is the workspace's rule, so it is
 *   asked rather than answered here.
 * @param isEventStreamConnected whether the push channel is live right now.
 *
 *   A function rather than a value because it is read on every paint and the answer changes
 *   under this window without anything here being told. It is the whole of what the footer
 *   still says about freshness — see where the `REFRESHED …` line used to be.
 */
internal class BookmarkListView(
    private val model: BookmarkListBackingViewModel,
    private val onEditBookmark: (Long) -> Unit,
    private val isEventStreamConnected: () -> Boolean = { true },
) {
    /** What the toolkit mounts. */
    val root: HTMLElement = element("div", "lp-list")

    /**
     * LPN-14's `#tag` control, in the one field that may say *not this one*.
     *
     * `allowExclude = true` because here a tag **is** a filter, which is the only place
     * that is true; `offerCreate = false` because this field filters a collection it
     * already holds, so offering to create a tag nobody uses is offering to filter to zero
     * results. `TagInputRules` states both.
     *
     * `everythingIsATag = false` for the same family of reasons and it is the **only** field
     * that says so: what is left in this box when somebody clicks away is a search over
     * title, URL and summary — a real thing this window is already showing results for — and
     * folding it into a filter chip would answer a question nobody finished asking. The two
     * flags agree, as they must: this is the field that cannot create a tag, so the one it
     * committed on the way out would match nothing.
     */
    private val search = TagInputField(
        placeholder = "Search, or #tag to filter",
        allowExclude = true,
        offerCreate = false,
        everythingIsATag = false,
        // Both commits are the reader saying *now* — and both are about to change the filter
        // themselves, so whatever is still waiting in [pendingQuery] has to land first or it
        // would be applied on top of the tag a moment later. See [onSearchTextChanged].
        onCommit = {
            flushQuery()
            if (it.excluded) model.onTagExcluded(it.tag) else model.onTagIncluded(it.tag)
        },
        onRemove = {
            flushQuery()
            model.onTagCleared(it.tag)
        },
        onTextChanged = { onSearchTextChanged(it) },
    )

    // ── The three joins, built once ──────────────────────────────────────────
    //
    // Members rather than made inside [condition], because a [Joiner] carries a lambda and a
    // data class compares one by identity — so a strip rebuilt from fresh joiners is never
    // equal to the strip before it, and `TagInputField.setCommitted` could not tell a repaint
    // that changed the chips from one that did not. Two of these are pressed to flip `MATCH`;
    // the third is the word an exclusion joins with, stated and not offered.

    /** Drawn while the filter means *all of these*; pressing it asks for any. */
    private val joinAnd = Joiner("AND", "Match any one of these instead") {
        model.onMatchChanged(FilterMatches.ANY)
    }

    /** Drawn while the filter means *any of these*; pressing it asks for all. */
    private val joinOr = Joiner("OR", "Match every one of these instead") {
        model.onMatchChanged(FilterMatches.ALL)
    }

    /** The join before an excluded chip. No press — see [condition]. */
    private val statedAnd = Joiner("AND")

    /**
     * *Tags ▾* — the button that raises the tag panel.
     *
     * The toolkit's trigger rather than one of our own, which buys three things a hand-rolled
     * pill would each have to reimplement and then keep in step: the chevron is an element
     * that takes `currentColor` and flips on open, the pressed state is keyed off
     * `aria-expanded` so the paint and the accessibility tree cannot drift, and the **accent
     * dot** already means *this control is narrowing what you are looking at* everywhere else
     * in the ecosystem. That dot is the whole of the canvas's "only non-defaults surface":
     * a filter you have set says so, and one you have not is quiet.
     */
    private val tagsButton = buildMenuTrigger(extraClass = "lp-list-trigger") {
        model.onTagPanelToggled(!(lastState?.ready?.tagPanelOpen ?: false))
    }

    /**
     * The tag panel — **built once and never replaced**, which is the whole of LPN-41.
     *
     * Everything the panel draws is torn down and rebuilt on every state, and that is right
     * for a cloud of chips. It was wrong for one thing in it, because [railFind] holds a caret
     * and *typing into it is what causes the repaint*: `onRailQueryChanged` publishes a state,
     * the window paints, and a panel rebuilt from scratch takes the field the reader is typing
     * into out of the document. A removed input is a blurred input, so the first character
     * landed and the second had nowhere to go — the field had to be clicked again between
     * every keystroke.
     *
     * So the panel is a member, and so is the box in its head that holds the field.
     * Everything after that box, and everything below the head, is still rebuilt wholesale —
     * they are buttons and chips, and none of them holds anything a reader would miss. See
     * [renderTagPanel] and [renderTagPanelHead] for where that line is drawn.
     *
     * Note what this is **not**: nothing here puts focus back after a repaint. A re-focus
     * would fight the reader — it moves the caret to the end and drops any selection — and it
     * would only be papering over a node whose identity changes when it should not.
     */
    private val tagPanel = AnchoredPanel(
        anchor = tagsButton,
        className = "lp-list-panel",
        maxWidthPx = TAG_PANEL_MAX_PX,
    ) { model.onTagPanelToggled(false) }

    /** The panel's top row. Its first child is permanent; see [renderTagPanelHead]. */
    private val railPanelHead = element("div", "lp-list-panel-head")

    /** The frame around *Find a tag…*, permanent because the field inside it is. */
    private val railFindBox = element("div", "lp-list-panel-find-box")

    /** *Find a tag…* — narrows the chips in the panel, and changes no filter. */
    private val railFind = textInput("Find a tag…", "lp-list-panel-find") {
        model.onRailQueryChanged(it)
    }

    /** *View ▾* — sort, grouping, the two AI knobs and the column count. */
    private val viewButton = buildMenuTrigger(extraClass = "lp-list-trigger") {
        model.onViewPanelToggled(!(lastState?.ready?.viewPanelOpen ?: false))
    }

    private val viewPanel = AnchoredPanel(
        anchor = viewButton,
        className = "lp-list-view",
        maxWidthPx = VIEW_PANEL_MAX_PX,
    ) { model.onViewPanelToggled(false) }

    /**
     * `TITLE · URL · SUMMARY` — what free text searches, said once and standing.
     *
     * A member because [renderBar] hides it at narrow widths. "Summary" covers the note and
     * the model's summary alike — see `filterBookmarks`, which explains why that is one word
     * rather than two.
     */
    private val searchHint = element("span", "lp-list-search-hint", "TITLE · URL · SUMMARY")

    /** `12 SHOWN`, beside the field that changes it. */
    private val shown = element("span", "lp-list-shown")

    /** *Reset*, drawn only while there is something to undo. */
    private val reset = button("Reset", "lp-list-reset") {
        // Dropped rather than flushed: *Reset* is asking for the free text to go, and a
        // keystroke still in flight would put it straight back a tenth of a second later.
        cancelQuery()
        model.onFilterReset()
    }

    private val body = element("div", "lp-list-body")
    private val footer = element("div", "lp-list-footer")
    private val banner = element("div", "lp-list-banner")

    /**
     * What the rows are placed on: one element the height of the whole list, holding only
     * the handful of cards near the viewport.
     *
     * See the virtualisation section below for why the rows are absolutely positioned on it
     * rather than laid out by the grid that used to be here.
     */
    private val canvas = element("div", "lp-list-canvas")

    /** The last state painted, so a resize can repaint it without waiting for the model. */
    private var lastState: State? = null

    /**
     * The free text typed but not yet handed to the model, or null when there is none.
     *
     * See [onSearchTextChanged]. Held rather than re-read off the input because the field is
     * allowed to change again before the timer fires, and what lands has to be the last thing
     * typed rather than whatever the input happens to hold when a stray flush runs.
     */
    private var pendingQuery: String? = null

    /** The `setTimeout` handle behind [pendingQuery], or null. */
    private var queryTimer: Int? = null

    /** The width the rows were last laid out at. See the observer in `init`. */
    private var lastWidth: Int = -1

    /** A repaint already booked for the next frame, or null. See [bookFrame]. */
    private var pendingFrame: Int? = null

    /** Whether the booked frame has to redo the whole window rather than only the rows. */
    private var pendingFullRender: Boolean = false

    /**
     * Where every row in the result set sits, whether or not it is in the DOM. The list's
     * whole geometry — see [layOut].
     */
    private var placements: List<Placement> = emptyList()

    /** [placements] by key, for the two lookups that are by row rather than by position. */
    private var placementsByKey: Map<String, Placement> = emptyMap()

    /** The rows currently in the DOM, by [Placement.key]. */
    private val rendered: MutableMap<String, HTMLElement> = mutableMapOf()

    /**
     * Measured card heights, by [heightKey].
     *
     * The cache is what makes the layout stop moving: a card measured once keeps its real
     * height for as long as the shape of the list is unchanged, so scrolling back up lands
     * where it did before. Dropped wholesale when [layoutSignature] changes, because a
     * different column width is a different height for every card in the list.
     */
    private val heights: MutableMap<String, Double> = mutableMapOf()

    /** What [heights] was measured under: change it and every measurement is stale. */
    private var layoutSignature: String = ""

    /**
     * What an unmeasured card is assumed to be, until it has been seen.
     *
     * Two running values rather than one, because the two kinds differ by a factor of three
     * and one average over both would be wrong for every card. They are only ever used for
     * rows nobody has scrolled to yet, and each is replaced by a real measurement the moment
     * its row is painted — so the cost of a bad guess is bounded by [settle], which corrects
     * the layout before the reader sees it.
     */
    private var collapsedGuess: Double = DEFAULT_COLLAPSED_PX
    private var expandedGuess: Double = DEFAULT_EXPANDED_PX

    /** A day header's height, measured once — they are all the same. */
    private var dayHeaderGuess: Double = 30.0

    /**
     * Where the reader last had this list scrolled to.
     *
     * Kept because **the browser can silently forget it**. The shell re-tiles its panes
     * whenever their number changes — opening an editor from a row is exactly that — and
     * re-tiling rebuilds each pane wrapper, which takes this window's scroller out of the
     * document and puts it back. A scroller that leaves the document has its `scrollTop`
     * reset to zero, so the reader opens an editor and finds the list they were halfway
     * down back at the top — or, since the rows are virtualised and still positioned for
     * where the reader was, finds nothing at all.
     *
     * **The toolkit now prevents this at the source** — `LayoutRenderer` reads every
     * scroller's position before its wipe and puts it back once the content renderers have
     * re-mounted, which is where the fix belongs and which every app on the toolkit gets.
     * What is left here is a backstop, and it earns its place for one reason: a *release*
     * build resolves the toolkit from its pinned version rather than from the sibling
     * checkout, so this is what holds until that pin has moved past the fix.
     *
     * Harmless once the pin has: the restore below is one comparison against a scroller
     * nothing reset.
     *
     * See [restoreScrollIfReset] for how the reset is told apart from a reader who simply
     * scrolled to the top, and [scrollTo], which is the only writer of both this and
     * `body.scrollTop`.
     */
    private var rememberedScroll: Double = 0.0

    /**
     * The row `j`/`k` was on last paint.
     *
     * Scrolling the focused row into view is right when the focus *moved* and wrong when
     * it did not: `space` expands a row in place, and a repaint that scrolled afterwards
     * would undo the one promise expanding-in-place makes.
     */
    private var scrolledTo: Long? = null

    /**
     * Which rows were expanded on the last paint.
     *
     * Kept for one comparison, in [renderRows]: whether the focused row's *own* expansion is
     * what changed. That is the difference between a focus that travelled — `j`, `k`, a click
     * on another row — and a row growing under a reader who is looking straight at it, and
     * only the first of the two may scroll.
     */
    private var lastExpandedIds: Set<Long> = emptySet()

    init {
        // The permanent spine of the tag panel. Assembled once here rather than on the first
        // paint, so there is exactly one place that says what the panel is made of and no
        // paint can find it half-built. See [tagPanel].
        railFindBox.appendChild(element("span", "lp-list-panel-find-glyph", "⌕"))
        railFindBox.appendChild(railFind)
        railPanelHead.appendChild(railFindBox)
        tagPanel.element.appendChild(railPanelHead)
        root.tabIndex = 0
        root.appendChild(buildHeader())
        root.appendChild(banner)
        root.appendChild(body)
        root.appendChild(footer)
        // On the window's own root rather than on the document: two lists can be open at
        // once, and a document-level listener would have to work out which one a keystroke
        // was meant for. Here the answer is where the focus is.
        root.addEventListener("keydown", { event -> onKeyDown(event as KeyboardEvent) })
        // Clicking anywhere in the rows gives the window the keyboard, so `j` works without
        // hunting for something to focus first. Links, buttons and fields are left alone —
        // they are already taking the click.
        body.addEventListener("mousedown", { event ->
            if (!(event.target as? HTMLElement).isInteractive()) root.focus()
        })
        // The other half of virtualisation: the rows near the viewport are the only ones in
        // the DOM, so scrolling has to bring the next ones in. Coalesced to a frame like
        // every other repaint here — a scroll fires far faster than the screen refreshes,
        // and painting more than once per frame is work nobody can see.
        //
        // Recording the position here is what makes [rememberedScroll] mean what it says:
        // this event is the browser telling us the scroller *moved*, and a scroller reset by
        // being taken out of the document does not fire it.
        body.addEventListener("scroll", {
            rememberedScroll = body.scrollTop
            bookFrame(fullRender = false)
        })
        // Two things on this surface are measurements rather than settings — how many chips
        // the two-row rail hides, and how many columns `AUTO` resolves to — so a window
        // that changes size has to be asked again.
        //
        // A `ResizeObserver` on the rows rather than a `resize` listener on the browser
        // window, and the difference is most of the cases: a pane is resized by dragging a
        // split bar, by being maximised, by the sidebar being collapsed, by a second window
        // opening beside it and by the reader changing their font size — and the browser
        // window is the same size through every one of them. The toolkit has a seam for
        // this too (`AppShellSpec.onPaneGeometryCommitted`), and it is narrower: it fires
        // for gestures the toolkit performed, so the font change and the sidebar are not
        // among them. Observing the element covers the lot with no shell wiring.
        observeWidth(body) { width ->
            // Guarded on the width actually changing, because a repaint can add or remove
            // the rows' scrollbar — which changes the width — and an unguarded observer
            // would oscillate between two layouts forever.
            if (width != lastWidth) {
                lastWidth = width
                // **Synchronously, here, and not in the frame below.** A pane whose width
                // changed is nearly always a pane the shell has just re-tiled, and re-tiling
                // takes this scroller out of the document — which zeroes it. A
                // `ResizeObserver` runs *before* the browser paints, so putting the position
                // back here puts it back in the same frame it was lost. Deferred into the
                // repaint instead, the reader gets one frame of an empty list: the rows are
                // still where they were, which is now thousands of pixels below a scroller
                // that thinks it is at the top.
                restoreScrollIfReset()
                // **Coalesced to the next frame**, and this is the fix for the freeze that
                // followed *closing a different window*. A pane being closed, opened, dragged
                // or maximised does not resize this one once: the observer fires repeatedly
                // as the layout settles, and each call used to rebuild every row in the
                // collection synchronously. Deferring to a frame collapses a burst into one
                // repaint, and the frame is the right unit because the reader cannot see
                // more than one anyway.
                bookFrame(fullRender = true)
            }
        }
    }

    // ── The filter bar ───────────────────────────────────────────────────────
    //
    // ── One row, whatever is applied ─────────────────────────────────────────
    //
    // The header used to be three bands stacked — a search row, a tag rail clipped to two
    // rows of chips, and a wrapping strip of six selects — and it ran from about 160 pixels
    // unfiltered to over 460 with the rail open. Every tag pressed moved the first bookmark
    // somewhere else on screen, and in a narrow pane the filter took a third of the window
    // from the thing it filters.
    //
    // What replaced it is a single row that does not change height:
    //
    //  - **the condition, in the field.** The chips were always here; what is new is the word
    //    between two of them saying how they join, which is a control — see [Joiner]. The
    //    `MATCH` select it replaces is gone.
    //  - **`Tags ▾`**, raising the cloud, its search, its sort and *Hide empty*. All four of
    //    those govern the cloud rather than the collection, so they belong inside the thing
    //    they govern rather than beside the filters.
    //  - **`View ▾`**, holding the five knobs and the column strip. Five of the six selects
    //    read a default at any given moment — *Newest*, *Day*, *Any time*, *Any*, *Any* — and
    //    a permanently drawn default looks exactly like a choice somebody made. In here, the
    //    accent dot on the button is what says one of them has moved.
    //  - **the count**, next to the field that changes it.
    //  - **`Reset`**, only when there is something to undo.
    //
    // The window's own name still says what it is filtered to — `paneLabel` reads
    // `filterSummary` — so the bar is free to say nothing when nothing has been added on top.

    private fun buildHeader(): HTMLElement {
        val header = element("div", "lp-list-header")

        // One box, holding the glyph, the chips-and-text field, and the standing reminder of
        // what free text searches. They used to be three siblings in a row, with the field
        // carrying the frame; now the frame is the box, because the reminder belongs *inside*
        // the field it describes.
        val field = element("div", "lp-list-field")
        field.appendChild(element("span", "lp-list-search-glyph", "⌕"))
        search.root.classList.add("lp-list-search-field")
        field.appendChild(search.root)
        field.appendChild(searchHint)
        // ── Where the ↻ used to be ───────────────────────────────────────────
        //
        // Gone in LPN-27, along with its "Ask the server again" tooltip. It existed because
        // nothing told this window anything: a bookmark saved on the phone, or from the
        // bookmarklet in another tab, was invisible here until somebody pressed it. There is
        // now a per-account event channel that says what changed, and the list re-reads on its
        // own — so the button had become an instruction to do manually what is already
        // happening, which is worse than no button at all because pressing it teaches you that
        // it was needed.
        //
        // `onRefreshRequested` stays on the model and stays called: pull-to-refresh on both
        // phones is the same intent, and it is the right one to keep there, because a gesture
        // somebody makes deliberately is different from a button that implies staleness.
        header.appendChild(field)
        header.appendChild(tagsButton)
        header.appendChild(viewButton)
        header.appendChild(shown)
        header.appendChild(reset)
        // The two panels are `fixed`, so where they sit in the document decides nothing about
        // where they are drawn — only that they are torn down with this window. See
        // [AnchoredPanel].
        header.appendChild(tagPanel.element)
        header.appendChild(viewPanel.element)
        return header
    }

    /**
     * The shell has finished a rerender pass. Put the scroll back if that pass took it.
     *
     * **The signal this window could not otherwise get.** Re-tiling rebuilds the pane
     * subtrees, which moves this scroller and zeroes it — and a pane that keeps its width
     * through the re-tile (a new window landing in the *other* column, say) resizes not at
     * all, so nothing else here would ever hear about it. The list would then sit at the top
     * with every row still positioned for where the reader had been: a window that has gone
     * blank, and stays blank until something else happens to repaint it.
     *
     * `onAfterRefresh` exists for exactly this class of loss — its own documentation names
     * the pane-subtree rebuild as the reason — and it is called at the end of the pass,
     * before the browser paints, so the position is back within the same frame.
     *
     * Cheap to call and idempotent, which is what that hook asks for: with nothing lost this
     * is one comparison. See [restoreScrollIfReset].
     */
    fun onShellRefreshed() {
        restoreScrollIfReset()
    }

    // ── The free text, applied a moment after the typing stops ───────────────
    //
    // Every other control on this bar changes the filter once per press, and the model is
    // built to recompute the whole window from the filter each time — see `publish`. The
    // search field is the one control that fires that per *character*, so a reader typing a
    // word asks for eight full re-derivations of a collection and eight repaints of the rows,
    // of which seven are for a prefix nobody wanted to see the results of.
    //
    // So the characters accumulate and one filter change follows the last of them. The delay
    // is short enough that it reads as the results keeping up rather than as a pause, and it
    // is the same idea `FILTER_WRITE_DELAY_MS` already applies one layer out to *storing* the
    // filter — this is the same restraint applied to *applying* it.
    //
    // ── What is deliberately not delayed ─────────────────────────────────────
    //
    // The `#tag` menu under the caret. It is drawn by `TagInputField` from the account's
    // vocabulary, which the field already holds, and it never consults the result set — so it
    // is already instant on the `input` event and this does not touch that path. Typing a tag
    // name feels the same as it did; what changes is only that the rows behind the menu stop
    // being re-filtered for `#k`, `#ko`, `#kot` on the way to `#kotlin`.

    /** A character was typed: remember it, and restart the clock. */
    private fun onSearchTextChanged(text: String) {
        pendingQuery = text
        queryTimer?.let { window.clearTimeout(it) }
        queryTimer = window.setTimeout({
            queryTimer = null
            pendingQuery?.let { model.onQueryChanged(it) }
            pendingQuery = null
        }, QUERY_DELAY_MS)
    }

    /** Apply whatever is waiting, now. Called by anything that must not be overtaken by it. */
    private fun flushQuery() {
        queryTimer?.let { window.clearTimeout(it) }
        queryTimer = null
        pendingQuery?.let { model.onQueryChanged(it) }
        pendingQuery = null
    }

    /** Throw away whatever is waiting. For *Reset*, which is asking for it to go. */
    private fun cancelQuery() {
        queryTimer?.let { window.clearTimeout(it) }
        queryTimer = null
        pendingQuery = null
    }

    /** Paint one state. */
    fun render(state: State) {
        lastState = state
        banner.hidden = true
        if (state.loading != null) {
            body.innerHTML = ""
            body.appendChild(element("div", "lp-list-empty", "Loading…"))
            return
        }
        state.failed?.let {
            body.innerHTML = ""
            body.appendChild(element("div", "lp-list-failed", it.message))
            return
        }
        val ready = state.ready ?: return

        search.setKnownTags(ready.knownTags)
        // The committed set is the window's tags, both kinds, in one list — which is what
        // makes the chip in the field and the chip in the panel the same fact rather than
        // two copies of it. The `AI` mark comes from the rail's own view of the result set,
        // so a tag reads the same in both places.
        val aiTags = ready.rail.filter { it.fromAi }.map { it.tag }.toSet()
        search.setCommitted(condition(ready, aiTags))
        // Only when the field is not the thing being typed into: this is here for a
        // restored window, whose stored query has to appear in a field nobody has touched.
        if (document.activeElement != search.textInput) search.setText(ready.filter.query)

        ready.message?.let {
            banner.textContent = it
            banner.hidden = false
        }

        renderBar(ready)
        renderTagPanel(ready)
        renderViewPanel(ready)
        renderRows(ready)
        renderFooter(ready)
    }

    // ── The condition, as chips joined by words ──────────────────────────────

    /**
     * The window's tags as one strip: `#framna AND #kotlin AND not #read-later`.
     *
     * Included tags lead and excluded ones follow, which is not merely tidy — it is what makes
     * the one clickable join mean something. Every join between two *included* tags is the
     * same fact, [WindowContent.BookmarkList.match], so they all read `AND` or all read `OR`
     * and pressing any of them flips the lot. Interleaving the exclusions would put joins that
     * cannot change between joins that can, with nothing to tell them apart but the word.
     *
     * An exclusion's join is drawn without a press for the same reason the `MATCH` select was
     * never offered for one: `not #a or not #b` is `not (#a and #b)`, which is not the filter
     * anybody assembling a list of chips is asking for, and the saved filter has no field that
     * could hold it.
     *
     * @param aiTags the tags the model coined, so a chip wears the same `AI` mark here as it
     *   does in the panel.
     */
    private fun condition(ready: State.Ready, aiTags: Set<String>): List<CommittedTag> {
        val filter = ready.filter
        val isAny = filter.match == FilterMatches.ANY
        // Only where it would change something. With one included tag "all of one" and "any of
        // one" are the same filter, so there is no join to draw and none to offer.
        val joinIncluded = if (isAny) joinOr else joinAnd
        val included = filter.includeTags.mapIndexed { index, tag ->
            CommittedTag(
                tag = tag,
                excluded = false,
                fromAi = tag in aiTags,
                joiner = joinIncluded.takeIf { index > 0 },
            )
        }
        val excluded = filter.excludeTags.mapIndexed { index, tag ->
            CommittedTag(
                tag = tag,
                excluded = true,
                fromAi = tag in aiTags,
                joiner = statedAnd.takeIf { index > 0 || included.isNotEmpty() },
            )
        }
        return included + excluded
    }

    // ── The bar's own two buttons, count and Reset ───────────────────────────

    private fun renderBar(ready: State.Ready) {
        val tags = ready.filter.includeTags.size + ready.filter.excludeTags.size
        setMenuTriggerLabel(
            trigger = tagsButton,
            text = if (tags == 0) "Tags" else "Tags · $tags",
            isChanged = tags > 0,
            title = "Browse and press tags",
        )
        setMenuTriggerOpen(tagsButton, ready.tagPanelOpen)
        setMenuTriggerLabel(
            trigger = viewButton,
            text = "View",
            // The dot is the whole of "only non-defaults surface": five knobs that read a
            // default are five knobs nobody needs to see, and one that does not is a fact
            // about what this window is showing that has to be on screen somewhere.
            isChanged = isViewChanged(ready.filter),
            title = "Sort, grouping and columns",
        )
        setMenuTriggerOpen(viewButton, ready.viewPanelOpen)
        shown.textContent = "${ready.shown} SHOWN"
        // Not merely disabled: a control that undoes nothing is a control somebody presses to
        // find out what it does, and this row has no space to spend on one.
        reset.hidden = !ready.isDirty
        // ── What goes when the pane is narrow, and in what order ─────────────
        //
        // The bar does not wrap, so something has to give, and the order is by how much each
        // thing is worth: the reminder first, because it is a reminder and not a control; the
        // count second, because the footer says the same number and the rows themselves are
        // the honest answer. The two buttons and *Reset* never go — they are the only way to
        // reach what is behind them.
        //
        // Measured rather than written as a media query, because what decides this is the
        // width of this *pane*: a list in one of three tiled panes is narrow on a very wide
        // screen. `observeWidth` already books a repaint when that changes.
        val width = bodyWidth()
        searchHint.hidden = width < HINT_MIN_PX
        shown.hidden = width < COUNT_MIN_PX
    }

    /** Whether anything inside *View* has moved off what a new window has. */
    private fun isViewChanged(filter: WindowContent.BookmarkList): Boolean {
        val fresh = WindowContent.BookmarkList()
        return filter.sort != fresh.sort ||
            filter.group != fresh.group ||
            filter.addedWithin != fresh.addedWithin ||
            filter.aiTags != fresh.aiTags ||
            filter.aiSummary != fresh.aiSummary ||
            filter.columns != fresh.columns
    }

    // ── The tag panel ────────────────────────────────────────────────────────

    /**
     * One chip, in one of three states, possibly wearing an `AI` mark.
     *
     * **The split chip**: the name includes, the minus excludes, and alt-click anywhere
     * does the same without aiming. A third press on a chip in either state turns it off —
     * which is the only way a chip can be its own undo.
     */
    private fun railChip(tag: RailTag, ready: State.Ready): HTMLElement {
        val state = ready.stateOf(tag.tag)
        // Included is one treatment, not two. An earlier version painted the first of
        // `includeTags` louder than the rest, on the theory that it was the window's own
        // tag and the others were added while browsing — but windows open empty and every
        // tag arrives the same way, so "first" only meant *clicked earliest and not yet
        // removed*. Nothing downstream agrees with it either: the match combines the tags
        // equally, and the title names the first two. Worse, clearing that tag moved the
        // emphasis to whichever one was next, which is a change in loudness with no change
        // in meaning behind it.
        val chip = element(
            "span",
            "lp-chip lp-chip-${state.name.lowercase()}",
        )
        val name = button(tag.tag, "lp-chip-name") {}
        name.addEventListener("click", { event ->
            val alt = (event as MouseEvent).altKey
            when {
                // Alt-click is the aimless version of the minus, and it is also how an
                // excluded chip is put back to off — otherwise the only way out of the
                // third state would be the panel.
                alt && state == TagChipState.EXCLUDED -> model.onTagCleared(tag.tag)
                alt -> model.onTagExcluded(tag.tag)
                state == TagChipState.INCLUDED -> model.onTagCleared(tag.tag)
                else -> model.onTagIncluded(tag.tag)
            }
        })
        chip.appendChild(name)
        if (tag.fromAi) chip.appendChild(element("span", "lp-tagfield-chip-ai", "AI"))
        chip.appendChild(element("span", "lp-chip-count", tag.uses.toString()))
        val minus = button("−", "lp-chip-minus") {
            if (state == TagChipState.EXCLUDED) model.onTagCleared(tag.tag) else model.onTagExcluded(tag.tag)
        }
        minus.title = if (state == TagChipState.EXCLUDED) "Stop excluding ${tag.tag}" else "Exclude ${tag.tag}"
        chip.appendChild(minus)
        return chip
    }

    /**
     * The tag panel — **repainted in place**, and only while it is up.
     *
     * [tagPanel] itself is permanent and so is the box holding *Find a tag…*; everything under
     * the head is built fresh, exactly as the rest of this window is. The split is not
     * tidiness, it is the difference between a search field that keeps its caret and one that
     * loses it on every character. See [tagPanel].
     *
     * ── What the cap is for, and why the numbers are stated ──────────────────
     *
     * *Hide empty* is not enough on an unfiltered collection: almost every tag is non-empty,
     * so hiding the empties still leaves hundreds and the panel becomes a page. So the panel
     * draws the first [State.Ready.railLimit] of the inactive chips by the current sort, then
     * a *Show more* saying how many are left, and the footer reads `48 OF 214 TAGS`. A
     * truncation that says so is a decision; one that does not is a panel that appears to be
     * lying about the vocabulary.
     *
     * **The active chips are exempt from all of it** — the cap, the sort, *Hide empty* and the
     * needle. They *are* the filter, and a control that can hide the thing you have to press
     * to undo the filter is a control that makes the filter a mystery.
     */
    private fun renderTagPanel(ready: State.Ready) {
        if (!ready.tagPanelOpen) {
            tagPanel.close()
            return
        }
        renderTagPanelHead(ready)
        // Everything below the head, gone. Walked from the head's sibling rather than by
        // count, so it stays right the day somebody puts a second permanent thing up there.
        var stale = railPanelHead.nextElementSibling
        while (stale != null) {
            val next = stale.nextElementSibling
            tagPanel.element.removeChild(stale)
            stale = next
        }

        // A–Z is the one sort where a cap on its own is useless: the first forty-eight are
        // the tags beginning with `a`, whatever the collection is about. So it grows a jump
        // strip, which narrows straight to a letter using the same needle the search box
        // uses — one mechanism, not two.
        if (ready.railSort == RailSorts.NAME) {
            val letters = element("div", "lp-list-panel-letters")
            ('A'..'Z').forEach { letter ->
                val needle = letter.lowercase()
                val on = ready.railQuery.trim().trimStart('#').lowercase() == needle
                val cell = button(
                    letter.toString(),
                    if (on) "lp-list-letter lp-list-letter-on" else "lp-list-letter",
                ) { model.onRailQueryChanged(if (on) "" else needle) }
                cell.setAttribute("aria-pressed", on.toString())
                letters.appendChild(cell)
            }
            tagPanel.element.appendChild(letters)
        }

        val active = ready.rail.filter { ready.stateOf(it.tag) != TagChipState.OFF }
        if (active.isNotEmpty()) {
            val activeRow = element("div", "lp-list-panel-active")
            activeRow.appendChild(element("span", "lp-list-panel-label", "ACTIVE"))
            active.forEach { tag ->
                val chip = railChip(tag, ready)
                chip.appendChild(button("×", "lp-chip-clear") { model.onTagCleared(tag.tag) })
                activeRow.appendChild(chip)
            }
            activeRow.appendChild(element("span", "lp-list-spacer"))
            activeRow.appendChild(button("Clear all", "lp-list-link") { model.onTagsCleared() })
            tagPanel.element.appendChild(activeRow)
            tagPanel.element.appendChild(element("div", "lp-list-panel-rule"))
        }

        val rest = ready.rail.filter { ready.stateOf(it.tag) == TagChipState.OFF }
        val grid = element("div", "lp-list-panel-grid")
        rest.take(ready.railLimit).forEach { tag ->
            val chip = railChip(tag, ready)
            if (tag.uses == 0) chip.classList.add("lp-chip-zero")
            grid.appendChild(chip)
        }
        val left = rest.size - minOf(rest.size, ready.railLimit)
        if (left > 0) {
            grid.appendChild(
                button("Show $RAIL_PAGE more · $left left", "lp-list-more") {
                    model.onRailMoreRequested()
                },
            )
        }
        if (rest.isEmpty() && active.isEmpty()) {
            grid.appendChild(element("div", "lp-list-panel-none", "No tag matches that."))
        }
        tagPanel.element.appendChild(grid)

        // It looks like a bug otherwise, and the first person to notice is the person who
        // narrowed the filter and watched every number fall. So the panel says it.
        val note = element("div", "lp-list-panel-footer")
        note.appendChild(
            element(
                "span",
                "lp-list-panel-note",
                "Counts are within the current filter, not the whole collection — which is why " +
                    "a tag can read 25 unfiltered and 9 in here. An excluded tag counts what it " +
                    "is holding back.",
            ),
        )
        note.appendChild(
            element("span", "lp-list-panel-count", "${ready.rail.size} OF ${ready.railTotal} TAGS"),
        )
        tagPanel.element.appendChild(note)

        // The condition, printed in words at the bottom of the panel it is being built in, so
        // the cloud and the strip in the field can never disagree about what has been said.
        if (active.isNotEmpty()) {
            val sentence = element("div", "lp-list-panel-condition")
            sentence.appendChild(element("span", "lp-list-panel-label", "FILTER"))
            sentence.appendChild(element("span", "lp-list-sentence", ready.sentence))
            sentence.appendChild(element("span", "lp-list-spacer"))
            sentence.appendChild(element("span", "lp-list-results", "${ready.shown} RESULTS"))
            tagPanel.element.appendChild(sentence)
        }
        tagPanel.open()
    }

    /**
     * The panel's top row: *Find a tag…*, a sort strip, *Hide empty*.
     *
     * The field is a member and is written to in place. Everything after it is rebuilt,
     * because a segmented strip and a switch are drawn *from* the state and hold nothing a
     * repaint could lose — and rebuilding them keeps this in step with how the rest of the
     * window is painted. The boundary is [railFindBox] itself rather than a count of children,
     * so it stays right if something permanent is ever added beside it.
     *
     * No *Collapse*: the panel is dismissed by pressing its button again, by `Escape`, or by
     * clicking the rows it is floating over — which is what every other menu on this surface
     * does, and one fewer control than the band it replaces had.
     */
    private fun renderTagPanelHead(ready: State.Ready) {
        // Never while it is the field being typed into: writing `value` there would put the
        // caret at the end of what the reader is halfway through. Exactly the guard `render`
        // puts on the search field above, for exactly that reason. What is left is the case
        // it is there for — a panel opened, or reopened after a close cleared the needle,
        // showing what the model actually holds.
        if (document.activeElement != railFind) railFind.value = ready.railQuery
        var stale = railFindBox.nextElementSibling
        while (stale != null) {
            val next = stale.nextElementSibling
            railPanelHead.removeChild(stale)
            stale = next
        }
        railPanelHead.appendChild(
            segmented(
                listOf(RailSorts.COUNT to "Count", RailSorts.NAME to "A–Z", RailSorts.RECENT to "Recent"),
                ready.railSort,
            ) { model.onRailSortChanged(it) },
        )
        railPanelHead.appendChild(
            toggle("Hide empty", ready.hideEmpty) { model.onHideEmptyChanged(it) },
        )
    }

    // ── The View panel ───────────────────────────────────────────────────────

    /**
     * `SORT`, `GROUP`, `ADDED`, `AI TAGS`, `AI SUMMARY` and the column strip, one per row.
     *
     * Six controls that were drawn permanently across the header, five of which read a
     * default at any given moment. Nothing distinguished a filter somebody had set from one
     * they had not, so the strip cost a whole band of the window to say *Newest, Day, Any
     * time, Any, Any* — five words nobody chose. In here, the accent dot on *View* is what
     * says one of them has moved, and pressing the button is what says which.
     *
     * `MATCH` is not among them any more. It is the word between two chips in the field now,
     * which is where the question it answers is actually asked — see [condition].
     *
     * Each value is still a native `<select>` behind a mono label rather than a menu of our
     * own. A hand-rolled dropdown *inside* a hand-rolled panel is a keyboard trap and a
     * focus problem twice over, and the browser's is already neither.
     *
     * The two AI knobs sit under `ADDED` rather than under `SORT`, because that is what they
     * are: they keep and drop rows, where `SORT` only orders them. They are drawn whether or
     * not this deployment has a model key — see `AiTagFilters` for why the list is not given a
     * second source of truth to hide them with.
     */
    private fun renderViewPanel(ready: State.Ready) {
        if (!ready.viewPanelOpen) {
            viewPanel.close()
            return
        }
        viewPanel.element.innerHTML = ""
        val filter = ready.filter
        viewPanel.element.appendChild(
            knob(
                "SORT",
                filter.sort,
                listOf(
                    BookmarkSorts.NEWEST to "Newest",
                    BookmarkSorts.OLDEST to "Oldest",
                    BookmarkSorts.TITLE to "Title",
                ),
            ) { model.onSortChanged(it) },
        )
        viewPanel.element.appendChild(
            knob(
                "GROUP",
                filter.group,
                listOf(BookmarkGroupings.DAY to "Day", BookmarkGroupings.NONE to "None"),
            ) { model.onGroupChanged(it) },
        )
        viewPanel.element.appendChild(
            knob(
                "ADDED",
                filter.addedWithin,
                listOf(
                    AddedWithins.ANY to "Any time",
                    AddedWithins.DAY to "24 hours",
                    AddedWithins.WEEK to "7 days",
                    AddedWithins.MONTH to "30 days",
                    AddedWithins.YEAR to "1 year",
                ),
            ) { model.onAddedWithinChanged(it) },
        )
        viewPanel.element.appendChild(
            knob(
                "AI TAGS",
                filter.aiTags,
                listOf(
                    AiTagFilters.ANY to "Any",
                    AiTagFilters.HAS to "Has tags",
                    AiTagFilters.MISSING to "No tags",
                ),
            ) { model.onAiTagsChanged(it) },
        )
        viewPanel.element.appendChild(
            knob(
                "AI SUMMARY",
                filter.aiSummary,
                listOf(
                    AiSummaryFilters.ANY to "Any",
                    AiSummaryFilters.HAS to "Has summary",
                    AiSummaryFilters.MISSING to "No summary",
                ),
            ) { model.onAiSummaryChanged(it) },
        )
        // No `ROWS`. There is one row shape now — see the note where `RowDensities` was.

        val columns = element("div", "lp-list-knob")
        columns.appendChild(element("span", "lp-list-knob-label", "COLUMNS"))
        columns.appendChild(element("span", "lp-list-spacer"))
        columns.appendChild(
            segmented(
                // Digits rather than the canvas's one-, two- and three-pane glyphs: those are
                // drawn shapes there, and the nearest characters a font can be relied on to
                // have are box-drawing ones that render as a missing-glyph square on most
                // systems. A number that says how many columns is not worse than a picture
                // of them.
                listOf("1" to "1", "2" to "2", "3" to "3", "${BookmarkColumns.AUTO}" to "AUTO"),
                filter.columns.toString(),
            ) { model.onColumnsChanged(it.toIntOrNull() ?: BookmarkColumns.AUTO) },
        )
        viewPanel.element.appendChild(columns)
        // The auto decision, made visible. A layout that picks a number on your behalf
        // should say what it picked and what it picked it from.
        viewPanel.element.appendChild(
            element(
                "div",
                "lp-list-auto",
                if (filter.columns == BookmarkColumns.AUTO) {
                    "${bodyWidth()} PX → ${BookmarkColumns.resolve(filter.columns, bodyWidth())} COLUMNS"
                } else {
                    "FIXED AT ${filter.columns} COLUMNS"
                },
            ),
        )
        viewPanel.open()
    }

    // ── The rows, and the window of them that is actually in the DOM ─────────
    //
    // ── Why this is virtualised, having not been ─────────────────────────────
    //
    // Every row used to be built on every paint. A row is about thirty nodes, so a
    // collection of a few thousand was a hundred thousand of them created, styled and laid
    // out — for a keystroke in the search box, for a filter knob, for a pane resizing
    // itself when a *different* window closed. It was seconds of a frozen tab, and no
    // amount of care about *how* the rows were built was going to change the order of it.
    //
    // So the rows near the viewport are the only ones that exist. [layOut] works out where
    // every row in the result set *would* be, [paintWindow] builds the few that are on
    // screen, and scrolling brings the next ones in. What is in the DOM stops being a
    // function of the collection's size and becomes a function of the window's height.
    //
    // ── The grid had to go with it, and that is a simplification ─────────────
    //
    // The multi-column layout used to be CSS grid over one-pixel implicit rows, with each
    // card told how many of them it spanned — masonry out of the auto-placement algorithm.
    // That needs every card in the document to be measured, which is exactly what cannot
    // happen any more. The placement is therefore done here, in arithmetic: each card goes
    // into whichever column currently ends soonest, which is what that whole mechanism was
    // an indirect way of asking for. The cards are absolutely positioned on [canvas], whose
    // height is the height the list would have.
    //
    // The three things the old shape got from CSS and this has to state itself:
    //
    //  - **an expanded row spans every column** (`.lp-list-grid > .lp-row-expanded`), so
    //    the summary keeps a readable measure. Here it starts below every column and puts
    //    every column below itself.
    //  - **the gap between cards** is still the stylesheet's number, read back off the
    //    canvas rather than written twice.
    //  - **day headers** span the width and sit between their groups.
    //
    // ── Heights are measured, not guessed, but guessed first ─────────────────
    //
    // A card's height is what its content came to, and nothing here can predict it — so a
    // virtual list has a chicken-and-egg problem the old one did not: the layout needs
    // heights, and heights need a layout. The answer is [settle]: lay out with a guess,
    // paint the window, measure what was painted, and lay out again if a measurement
    // disagreed — anchored on the row at the top of the viewport so the correction happens
    // *under* the reader rather than moving what they are looking at. Measured heights are
    // cached, so a row scrolled past once has a real height from then on.

    private fun renderRows(ready: State.Ready) {
        // Before anything is read off the scroller: a re-tiled pane may have zeroed it
        // between the last paint and this one, and every line below would then be
        // preserving a position nobody chose. See [restoreScrollIfReset].
        restoreScrollIfReset()
        // **Read before the tear-down, written after it.** The claim that expanding a row
        // costs nothing rests on this line and not on the container merely surviving:
        // emptying an element collapses its content to nothing, at which point the browser
        // clamps `scrollTop` to zero and the reader is back at the top of the list. The
        // scroller staying put is necessary and is not sufficient.
        val scroll = body.scrollTop
        if (ready.shown == 0) {
            teardown()
            body.innerHTML = ""
            body.appendChild(element("div", "lp-list-empty", emptyWords(ready)))
            return
        }
        // The canvas outlives a repaint, unlike everything on it: it is what `scrollTop`
        // is measured against, and replacing it would be the tear-down the line above
        // exists to survive.
        if (canvas.parentElement !== body) {
            body.innerHTML = ""
            body.appendChild(canvas)
        }
        // A row is a pure function of the state, so a state change rebuilds the rows rather
        // than patching them — the rule the rest of `:web` follows. It is affordable here
        // *because* of the window: this throws away a screenful, not a collection.
        teardown()
        val columns = BookmarkColumns.resolve(ready.filter.columns, bodyWidth())
        // The modifier moves from the grid to the canvas. Every rule that reads it is a
        // descendant selector (`.lp-list-cards .lp-row-prose-clamp`), so it goes on carrying
        // exactly what it carried. It no longer means "this is a card" — every row is one —
        // only "this card is sharing the width", which is what the rules under it are about.
        canvas.className = buildString {
            append("lp-list-canvas")
            if (columns > 1) append(" lp-list-cards")
        }
        applyLayout(ready)
        scrollTo(scroll)
        paintWindow()
        // The row whose expansion changed on this paint, if any — the one thing on screen the
        // reader is certainly looking at, and therefore the one the relayout has to hold
        // still. See [anchor].
        val toggled = (ready.expandedIds - lastExpandedIds).singleOrNull()
            ?: (lastExpandedIds - ready.expandedIds).singleOrNull()
        settle(ready, keepRowAtTop = true, hold = toggled)
        // ── Only when the focus moved, and never when a row merely grew ──
        //
        // "The focus moved" used to be the whole test, and it is not enough, because
        // **expanding a row is also focusing it** — `onRowExpandToggled` sets `focusedId`
        // so that the keyboard and the mouse cannot end up with two ideas of which row is
        // current. So the test could not tell `j` from a press on *Show more*, and pressing
        // *Show more* on a row that was not already focused fell through to [revealRow],
        // which scrolled — undoing the one promise expanding in place makes.
        //
        // It survived this long because the gesture that reached it was rare: `space` acts on
        // a row the reader had already focused, so the focus did not move. *Show more* made
        // it the ordinary path, and a row carrying both prose blocks made the jump large
        // enough to lose your place entirely — six hundred pixels of expansion against a five
        // hundred pixel window.
        //
        // A row whose own expansion changed on this paint is expanding (or collapsing) in
        // place, and that is the case that must not scroll. `settle` has already held the
        // list still around it.
        val focus = ready.focusedId
        val expandedInPlace = focus != null && (focus in ready.expandedIds) != (focus in lastExpandedIds)
        if (focus != scrolledTo && !expandedInPlace) focus?.let { revealRow(it) }
        // Both recorded whatever was decided above, so the next paint compares against what
        // actually happened rather than against the last paint that scrolled.
        scrolledTo = focus
        lastExpandedIds = ready.expandedIds
    }

    /** Drop every row currently in the DOM. A screenful, by construction. */
    private fun teardown() {
        rendered.values.forEach { it.remove() }
        rendered.clear()
    }

    /**
     * Work out where every row in the result set sits, and give [canvas] the height they
     * come to.
     *
     * Over the whole result set rather than the visible part, and it has to be: the
     * scrollbar is the reader's sense of how much there is, so it must describe the list
     * rather than the window onto it. It is arithmetic over a list already in memory — no
     * DOM is touched and nothing is measured — which is what makes running it several times
     * during a [settle] cheap enough not to think about.
     */
    private fun applyLayout(ready: State.Ready) {
        placements = layOut(ready)
        placementsByKey = placements.associateBy { it.key }
        canvas.style.height = "${placements.maxOfOrNull { it.top + it.height } ?: 0.0}px"
    }

    /** The placement of every row, in order, top to bottom of each group. */
    private fun layOut(ready: State.Ready): List<Placement> {
        val columns = BookmarkColumns.resolve(ready.filter.columns, bodyWidth())
        val width = canvasWidth()
        val gap = gapPx()
        val columnWidth = ((width - gap * (columns - 1)) / columns).coerceAtLeast(1.0)
        forgetHeightsIfShapeChanged(columns, columnWidth)

        val out = mutableListOf<Placement>()
        var y = 0.0
        ready.groups.forEach { group ->
            if (ready.isGrouped) {
                out += Placement(
                    key = "day:${group.dayStartMillis}",
                    group = group,
                    top = y,
                    height = dayHeaderGuess,
                    left = 0.0,
                    width = width,
                )
                y += dayHeaderGuess
            }
            // Where each column has got to. A card goes into whichever has got least far,
            // which is the masonry the grid-span trick was an indirect way of asking for.
            val tops = DoubleArray(columns) { y }
            group.bookmarks.forEach { bookmark ->
                val expanded = bookmark.id in ready.expandedIds
                val height = heightOf(bookmark.id, expanded)
                if (expanded && columns > 1) {
                    // The full-width case. Below everything, and everything below it — an
                    // expanded card is a horizontal rule that happens to have content.
                    val top = tops.maxOrNull() ?: y
                    out += Placement(
                        key = rowKey(bookmark.id),
                        bookmark = bookmark,
                        expanded = true,
                        top = top,
                        height = height,
                        left = 0.0,
                        width = width,
                    )
                    val bottom = top + height + gap
                    for (index in tops.indices) tops[index] = bottom
                } else {
                    val column = tops.indices.minByOrNull { tops[it] } ?: 0
                    out += Placement(
                        key = rowKey(bookmark.id),
                        bookmark = bookmark,
                        expanded = expanded,
                        top = tops[column],
                        height = height,
                        left = column * (columnWidth + gap),
                        width = columnWidth,
                    )
                    tops[column] += height + gap
                }
            }
            y = tops.maxOrNull() ?: y
        }
        return out
    }

    /**
     * Build the rows that are on screen, drop the ones that have left, and put the rest
     * where [layOut] said.
     *
     * The overscan is a viewport's worth in each direction, so an ordinary scroll finds its
     * rows already built and only a flung one can outrun the frame. Cheap either way: the
     * rows are rebuilt from the state they were always rebuilt from, there are just a few
     * dozen of them.
     */
    private fun paintWindow() {
        val ready = lastState?.ready ?: return
        val viewport = body.clientHeight.toDouble()
        // **A scroller with no height cannot say what is visible, so everything is.** That
        // is a window not yet laid out, and a view rendered outside the document — which is
        // how this file is tested. Answering "nothing is visible" would be indistinguishable
        // from a bug, and it would be one in the case that matters.
        val unbounded = viewport <= 0.0
        val overscan = maxOf(viewport, MIN_OVERSCAN_PX)
        val from = body.scrollTop - overscan
        val to = body.scrollTop + viewport + overscan

        val wanted = placements.filter { unbounded || (it.top < to && it.top + it.height > from) }
        val keys = wanted.mapTo(mutableSetOf()) { it.key }
        rendered.keys.toList().forEach { key ->
            if (key !in keys) rendered.remove(key)?.remove()
        }
        // The rows built by *this* pass, which are the only ones whose clamp has not been
        // asked about yet. See below.
        val fresh = mutableListOf<HTMLElement>()
        wanted.forEach { placement ->
            val node = rendered.getOrPut(placement.key) {
                val built = placement.bookmark?.let { row(it, ready) }
                    ?: dayHeader(checkNotNull(placement.group))
                canvas.appendChild(built)
                fresh += built
                built
            }
            node.style.top = "${placement.top}px"
            node.style.left = "${placement.left}px"
            node.style.width = "${placement.width}px"
        }
        // After the widths above and before [measure] reads a height, because it sits
        // between the two: a clamp bites or does not at the width the row was just given,
        // and revealing the button changes the height that is about to be recorded.
        //
        // **Only the rows just built**, and that is the whole of what makes a [settle] cheap.
        // Asking a row whether its clamp cut anything means reading `scrollHeight` right
        // after writing a style, which forces the browser to lay the document out there and
        // then — so doing it for every row on screen, on each of up to three settle passes,
        // was a hundred-odd synchronous layouts for one keystroke. A row that survived the
        // pass has the width it already had and the same answer it already gave; only a row
        // this pass created has never been asked. A width that genuinely changed drops the
        // height cache and rebuilds every row — see [forgetHeightsIfShapeChanged] — so the
        // case this skips cannot arise without the rows being fresh anyway.
        fresh.forEach { revealShowMore(it) }
    }

    /**
     * Show this row's *Show more*, if a clamp actually cut something.
     *
     * `scrollHeight > clientHeight` is the question "is there text below the fold of this
     * box", which is exactly what a line clamp makes true and nothing else here can answer:
     * how many lines a summary comes to is a function of the width the row was placed at,
     * the reader's font size and the words themselves. So it is asked of the DOM, per row,
     * after the row has a width — and it is asked of the *title* as well, because a title
     * long enough to be cut is the other thing expanding a row puts back.
     *
     * One class on the row rather than a style on the button, so the stylesheet keeps the
     * decision about what *hidden* looks like.
     */
    private fun revealShowMore(row: HTMLElement) {
        val more = row.querySelector(".lp-row-more") ?: return
        val cut = row.querySelectorAll(".lp-row-prose-clamp .lp-row-prose-text, .lp-row-title")
        val overflows = (0 until cut.length).any { index ->
            val box = cut.item(index) as? HTMLElement ?: return@any false
            box.scrollHeight > box.clientHeight + 1
        }
        (more as HTMLElement).hidden = !overflows
    }

    /**
     * Measure what was just painted, and lay out again if the guesses were wrong.
     *
     * ── Anchored, which is the whole difficulty ──────────────────────────────
     *
     * Correcting a height changes where everything below it sits — and, if the correction
     * is above the viewport, where everything the reader is *looking at* sits. So the row
     * at the top of the viewport is remembered before the relayout and the scroll is put
     * back onto it afterwards: the list rearranges itself around a fixed point rather than
     * sliding under the cursor.
     *
     * ── …except while the reader is the one scrolling ────────────────────────
     *
     * [keepRowAtTop] is false on the scroll path, and that is not a shortcut — anchoring
     * there is actively wrong. Anchoring *writes* `scrollTop`, and during a scrollbar drag
     * the browser is deriving `scrollTop` from where the mouse is: every write moves the
     * thumb out from under the cursor, the drag applies its next delta from the displaced
     * position, and the error accumulates until the thumb is nowhere near the pointer.
     *
     * When the reader is driving the scroller, the scroller *is* the fixed point, so the
     * measurements are taken and the layout corrected underneath a position nothing here
     * touches. Anchoring is for a layout that changed for some other reason — a filter, a
     * knob, a pane resizing — where the reader has not asked to move and must not be.
     *
     * ── Bounded, because it is a fixed point iteration ───────────────────────
     *
     * Each pass can pull new rows into the window, which are themselves guesses, so this
     * could in principle chase its own tail. In practice one pass settles it — the second
     * only has the rows the first pulled in — and the cap is there so that a pathological
     * case is a slightly wrong layout rather than a hung tab. Whatever is left over is
     * corrected on the next scroll, which repaints anyway.
     */
    private fun settle(ready: State.Ready, keepRowAtTop: Boolean, hold: Long? = null) {
        repeat(SETTLE_PASSES) {
            if (!measure()) return
            val anchor = if (keepRowAtTop) anchor(hold) else null
            applyLayout(ready)
            anchor?.let { (key, offset) ->
                // `+`, and it was a `-` for a long time. [anchor] hands back how far *into*
                // the row the scroll had got — `scroll - top` — so putting the reader back
                // where they were is that same distance past the row's new top. Subtracting
                // it lands on `2 * top - scroll`, which is only the right answer when the
                // offset is nought, i.e. when the anchor row's top happened to be exactly at
                // the fold. Every other position was thrown off by twice the offset.
                //
                // It went unnoticed because for a long time nothing much moved: the scroll
                // path does not anchor at all (see [keepRowAtTop]), and a collapsed row was
                // 84px tall, so the error was bounded by a row that barely mattered.
                // Expanding a row into a summary makes it hundreds of pixels, and *Show more*
                // made expanding the ordinary gesture rather than the rare one.
                placementsByKey[key]?.let { scrollTo(it.top + offset) }
            }
            paintWindow()
        }
    }

    /**
     * Read the real height of every rendered row into the cache.
     *
     * @return whether anything disagreed with what the layout assumed, which is the only
     *   reason to lay out again.
     */
    private fun measure(): Boolean {
        var moved = false
        rendered.forEach { (key, node) ->
            val placement = placementsByKey[key] ?: return@forEach
            // `offsetHeight` is the content height because nothing here writes one: the
            // rows are given a top, a left and a width, and never a height.
            val height = node.offsetHeight.toDouble()
            // Zero is a row the browser has not laid out — a detached view — rather than a
            // row of no height. Recording it would make every card in the list sit on top
            // of every other.
            if (height <= 0.0) return@forEach
            val bookmark = placement.bookmark
            if (bookmark == null) {
                if (abs(dayHeaderGuess - height) > TOLERANCE_PX) {
                    dayHeaderGuess = height
                    moved = true
                }
                return@forEach
            }
            val cacheKey = heightKey(bookmark.id, placement.expanded)
            if (abs((heights[cacheKey] ?: -1.0) - height) > TOLERANCE_PX) {
                heights[cacheKey] = height
                // And it teaches the guess, so the rows nobody has scrolled to yet are
                // placed with a number this collection actually produced rather than with a
                // constant written here.
                if (placement.expanded) {
                    expandedGuess = blend(expandedGuess, height)
                } else {
                    collapsedGuess = blend(collapsedGuess, height)
                }
                moved = true
            }
        }
        return moved
    }

    /**
     * Move the scroller, and remember that we meant to.
     *
     * The one writer of `body.scrollTop`, so that [rememberedScroll] describes every
     * deliberate move — the reader's own, through the scroll listener, and this window's
     * own, through here. Read back rather than stored from the argument, because the
     * browser clamps a position past the end of the list and the clamped value is the one
     * that is true.
     */
    private fun scrollTo(top: Double) {
        body.scrollTop = top
        rememberedScroll = body.scrollTop
    }

    /**
     * Put the scroll back where it was if the document took it away.
     *
     * ── How the reset is told apart from a reader scrolling to the top ───────
     *
     * A scroller moved by anybody — the reader, or [scrollTo] — fires a `scroll` event, and
     * that event is what keeps [rememberedScroll] current. A scroller reset by leaving the
     * document fires **nothing**. So "the DOM says nought and we were told nothing" is
     * exactly the signature of the reset, and a reader who genuinely scrolled to the top has
     * already set [rememberedScroll] to nought themselves.
     *
     * Restoring past the end of a list that has since shortened is harmless: the browser
     * clamps, which is the same answer it would have given anyway.
     *
     * The residual case is a reader scrolling to the very top in the same frame as a filter
     * change — `scroll` is delivered asynchronously, so this could see the old position and
     * put them back. One frame, and the next scroll event corrects it. Worth naming, not
     * worth machinery: the alternative is losing the reader's place every time they open an
     * editor, which is the common gesture rather than the rare coincidence.
     *
     * ── What this does not catch ─────────────────────────────────────────────
     *
     * Nothing, now — but only because it is not the last line of defence. `onAfterRefresh`
     * is what calls this for a re-tile (see [onShellRefreshed]), and the toolkit fires that
     * whether or not this pane's geometry changed. The `ResizeObserver` call is the earlier
     * of the two and covers the resizing case a frame sooner; neither is relied on alone.
     *
     * That pairing is deliberate rather than belt-and-braces: a window opening into the
     * *other* column re-tiles this pane without changing its width, so the resize path never
     * fires for it — which is exactly the case that painted an empty list.
     */
    private fun restoreScrollIfReset() {
        if (rememberedScroll > 0.0 && body.scrollTop == 0.0) body.scrollTop = rememberedScroll
    }

    /**
     * The row the relayout should stay still around, and how far into it the scroll has got.
     *
     * The row at the top of the viewport, ordinarily — it is a decent proxy for *what the
     * reader is looking at* when nothing in particular has their attention.
     *
     * [hold] is the case where something does. A row that has just been expanded or collapsed
     * is the row the reader is looking straight at, and holding *it* still is what
     * expanding-in-place means; the top of the viewport is then the wrong fixed point, and
     * wrong by however much the layout above it has been re-measured in the same pass. That
     * error is small and it is not zero — cached heights above the fold are re-measured as
     * rows re-enter the overscan window, and the anchor row inherits the drift — so the
     * difference between the two choices is the difference between a row that stays under
     * the cursor and one that creeps out from under it.
     */
    private fun anchor(hold: Long? = null): Pair<String, Double>? {
        val scroll = body.scrollTop
        hold?.let { id ->
            placementsByKey[rowKey(id)]?.let { return it.key to (scroll - it.top) }
        }
        val first = placements.firstOrNull { it.top + it.height > scroll } ?: return null
        return first.key to (scroll - first.top)
    }

    /**
     * Scroll [id] into view, `block: 'nearest'`, **without asking the DOM** — which is the
     * point: `j` can move to a row that is not built yet, and `scrollIntoView` on a node
     * that does not exist does nothing at all. The placement knows where the row is whether
     * or not anything has been painted for it.
     */
    private fun revealRow(id: Long) {
        val placement = placementsByKey[rowKey(id)] ?: return
        val viewport = body.clientHeight.toDouble()
        if (viewport <= 0.0) return
        val scroll = body.scrollTop
        val below = placement.top + placement.height > scroll + viewport
        when {
            // Above the window: its top to the top.
            placement.top < scroll -> scrollTo(placement.top)
            // **Taller than the window**, which an expanded row carrying a full summary
            // frequently is. `block: 'nearest'` for an oversized element means aligning its
            // top, not its bottom: scrolling the bottom into view puts the reader at the end
            // of a row whose title is now off the top of the window — shown the thing they
            // were not looking for and denied the thing they were.
            below && placement.height > viewport -> scrollTo(placement.top)
            // Below it, and short enough to fit: its bottom to the bottom.
            below -> scrollTo(placement.top + placement.height - viewport)
            else -> return
        }
        paintWindow()
    }

    /** What a card of this shape is assumed to be until it has been measured. */
    private fun heightOf(id: Long, expanded: Boolean): Double =
        heights[heightKey(id, expanded)] ?: if (expanded) expandedGuess else collapsedGuess

    private fun heightKey(id: Long, expanded: Boolean): String = "$id:$expanded"

    private fun rowKey(id: Long): String = "row:$id"

    /**
     * Drop every measurement when the shape of a card changes.
     *
     * A card's height is a function of the width it is laid out at, so a narrower column or
     * a different column count makes every cached number a lie — and a lie in the direction
     * that matters, since the list would be laid out from heights no card has any more.
     * Cheaper to re-measure a screenful than to keep a cache that has to be reasoned about.
     *
     * **The guesses go with them**, which is less obvious and was a real mistake to leave
     * out: a guess learned at one column width places the whole collection wrongly at
     * another, and a reader who then drags the scrollbar lands a long way from where they
     * aimed. Back to the defaults is not a good estimate either, but it is a *bounded* one,
     * and [settle] replaces it out of the first screenful it measures.
     */
    private fun forgetHeightsIfShapeChanged(columns: Int, columnWidth: Double) {
        val signature = "$columns|${columnWidth.toInt()}"
        if (signature == layoutSignature) return
        layoutSignature = signature
        heights.clear()
        collapsedGuess = DEFAULT_COLLAPSED_PX
        expandedGuess = DEFAULT_EXPANDED_PX
    }

    /** The width the cards are placed across. */
    private fun canvasWidth(): Double =
        canvas.clientWidth.takeIf { it > 0 }?.toDouble() ?: bodyWidth().toDouble()

    /**
     * The gap between two cards, as the stylesheet writes it.
     *
     * Read back rather than hardcoded, for [clipToTwoRows]' reason: the number belongs to
     * the stylesheet and two copies of it drift. It is declared as a `column-gap` on the
     * canvas, which has no effect on absolutely positioned children and every effect on
     * being able to read it back in pixels — a custom property would come back as the
     * `rem` string it was written as.
     */
    private fun gapPx(): Double =
        window.getComputedStyle(canvas).getPropertyValue("column-gap")
            .removeSuffix("px").trim().toDoubleOrNull() ?: 0.0

    /**
     * One row's place in the virtual list.
     *
     * Exactly one of [bookmark] and [group] is set: a card, or the day header above a run
     * of them.
     */
    private class Placement(
        val key: String,
        val top: Double,
        val height: Double,
        val left: Double,
        val width: Double,
        val bookmark: Bookmark? = null,
        val group: BookmarkDayGroup? = null,
        val expanded: Boolean = false,
    )

    /** Why the list is empty, in the words the state actually justifies. */
    private fun emptyWords(ready: State.Ready): String = when {
        ready.total == 0 ->
            "Nothing saved yet. The Settings cogwheel has a button to drag to your bookmarks " +
                "bar — press it on any page to keep it."

        else -> "No bookmarks match this filter. ${ready.total} are outside it."
    }

    private fun dayHeader(group: BookmarkDayGroup): HTMLElement {
        val header = element("div", "lp-list-day")
        header.appendChild(element("span", "lp-list-day-label", dayWords(group)))
        header.appendChild(element("span", "lp-list-day-rule"))
        header.appendChild(element("span", "lp-list-day-count", group.bookmarks.size.toString()))
        return header
    }

    /**
     * *TODAY*, *YESTERDAY*, or the date.
     *
     * The relative words are worth having for exactly two days and no more: past that,
     * "5 days ago" is arithmetic the reader has to do to get back to a date, and the date
     * was what they wanted. Formatted by the browser, which knows how this reader writes
     * one.
     */
    private fun dayWords(group: BookmarkDayGroup): String = when (group.daysAgo) {
        0 -> "TODAY"
        1 -> "YESTERDAY"
        else -> dayHeaderDate(group.dayStartMillis).uppercase()
    }

    private fun row(bookmark: Bookmark, ready: State.Ready): HTMLElement {
        val expanded = bookmark.id in ready.expandedIds
        val item = element(
            "div",
            buildString {
                append("lp-row")
                if (expanded) append(" lp-row-expanded")
                if (bookmark.id == ready.focusedId) append(" lp-row-focused")
            },
        )
        item.setAttribute("data-bookmark", bookmark.id.toString())
        item.addEventListener("click", { model.onRowFocused(bookmark.id) })

        // No tile. A rounded square holding two letters of the domain was here, standing in
        // for the favicon this app does not fetch — and two letters do not tell one domain
        // from the next in the way an icon does: `example27.com` and `example49.com` are both
        // `EX`, and so is every other domain sharing a first syllable. What it reliably did
        // was take a column's width from the title. The domain is written out in the
        // metadata line, in full, which is the thing the tile was an abbreviation of.

        val main = element("div", "lp-row-main")
        val head = element("div", "lp-row-head")
        head.appendChild(
            button(if (expanded) "⌄" else "›", "lp-row-caret") {
                model.onRowExpandToggled(bookmark.id)
            },
        )
        head.appendChild(externalLink(bookmark))
        // Everything that is *about* the bookmark rather than the bookmark itself, in one
        // box — and the box is the whole point of it.
        //
        // A one-line row keeps all of this beside the title, so `.lp-row-meta` is
        // `display: contents` there and these are the head's own flex children exactly as
        // they were. A **card** stacks, and a card is where a wrapper earns its place: the
        // head wraps, and with the domain, the time and *Edit* as loose children the only
        // thing that could start the second line was whichever child happened not to fit —
        // which was the caret, stranded at the head of the metadata line as a `›` in front
        // of the domain, pointing at nothing. One box wraps as one thing, so the caret
        // stays with the title it opens and the line below is the metadata entire.
        val meta = element("div", "lp-row-meta")
        // Not when the title *is* the URL. A bookmark whose page had no title renders one —
        // either because the field is empty and `externalLink` falls back, or because the
        // server stored the URL as the title, and both are the same row to look at. The
        // domain is then the front of the line above, printed again three millimetres below
        // it, and the row says `share.google` twice while saying nothing else at all.
        if (bookmark.title.isNotBlank() && bookmark.title != bookmark.url) {
            meta.appendChild(element("span", "lp-row-domain", domainOf(bookmark.url)))
        }
        meta.appendChild(element("span", "lp-list-spacer"))
        // One slot, chosen by `aiState`. The badge *replaces* the time rather than sitting
        // beside it: a row that is still being read has one thing worth saying at its right
        // edge, and 08:12 is not it.
        meta.appendChild(
            if (bookmark.isAwaitingSummary) {
                element("span", "lp-row-summarising", "SUMMARISING")
                    .also { it.title = "Reading the page and writing a summary." }
            } else {
                element("span", "lp-row-time", timeWords(bookmark.createdAt))
            },
        )
        val actions = element("div", "lp-row-actions")
        actions.appendChild(
            button("Edit", "lp-row-action") { onEditBookmark(bookmark.id) },
        )
        meta.appendChild(actions)
        head.appendChild(meta)
        main.appendChild(head)

        if (bookmark.tags.isNotEmpty()) {
            val tags = element("div", "lp-row-tags")
            // ── Every tag, and no *More* ──
            //
            // There used to be a cap of four and a chip saying there were others. That cap
            // was a one-line row's problem: the chips shared a line with the title and the
            // metadata, so the strip had to clip and something had to say it had. It clipped
            // badly — `#accountabili`, `#android-autc`, half a word each — and a chip cut in
            // half is worse than a chip not drawn.
            //
            // The tags have their own line now, under the metadata, and they wrap onto a
            // second when they need one. So there is nothing to hide and nothing to count:
            // the row simply says what it is filed under. A collection whose bookmarks carry
            // a dozen tags each would make this a tall block, and the answer then is a clip
            // measured the way the rail measures its own — not a number written here.
            val strip = element("div", "lp-row-tags-shown")
            val ai = bookmark.aiTags.toSet()
            bookmark.tags.forEach { tag ->
                val chip = element(
                    "span",
                    if (ready.stateOf(tag) == TagChipState.OFF) "lp-row-tag" else "lp-row-tag lp-row-tag-on",
                )
                chip.appendChild(element("span", "lp-row-tag-name", tag))
                // The same mark the editor's chips wear, and the same rule: a tag says who
                // put it there and is otherwise an ordinary tag.
                if (tag in ai) chip.appendChild(element("span", "lp-tagfield-chip-ai", "AI"))
                chip.addEventListener("click", { event ->
                    event.stopPropagation()
                    if ((event as MouseEvent).altKey) model.onTagExcluded(tag) else model.onTagIncluded(tag)
                })
                strip.appendChild(chip)
            }
            tags.appendChild(strip)
            main.appendChild(tags)
        }

        // ── Both blocks, always, and the collapsed row clamps them ──
        //
        // A collapsed row used to show *one* — the model's summary if there was one, the
        // owner's note otherwise — on the theory that two clamped paragraphs is neither
        // readable nor scannable. What it actually meant was that a bookmark somebody had
        // written their own note on hid that note behind a summary a machine wrote about the
        // same page, and the one thing on the row nobody else could have written was the one
        // thing you had to open the row to see.
        //
        // So both, in the order a person's own words come first. Two lines each is a
        // paragraph's worth between them and still a row you can run your eye down; the
        // pills keep them tellable apart, which was always the point of the pair of fields.
        //
        // The note leads even while a summary is arriving, which is the case that used to
        // show skeletons over a note that was already there — the row no longer has to
        // choose, so it no longer has to rearrange itself when the summary lands.
        if (bookmark.summary.isNotBlank()) {
            main.appendChild(prose("NOTE", "lp-row-note-pill", bookmark.summary, clamp = !expanded))
        }
        // Whatever stands in the model's half of the row: the summary, the two bars that say
        // one is coming, the line that says one is not, or nothing.
        aiBlock(bookmark, clamp = !expanded)?.let { main.appendChild(it) }

        if (expanded) {
            main.appendChild(expandedActions(bookmark))
        } else {
            // On every collapsed row, and hidden until something is measured to actually
            // overflow. Built unconditionally rather than only where there is prose, because
            // the title clamps too: a bookmark with a four-line name and no summary at all
            // has something behind the fold, and it is the thing the row is *for*.
            main.appendChild(showMore(bookmark.id))
        }

        item.appendChild(main)
        return item
    }

    /**
     * *Show more*, at the end of the clamped prose.
     *
     * The caret at the head of the title has always opened a row and goes on doing so. This
     * is not a second control for a different thing; it is the same one where the eye
     * already is. An ellipsis is a statement that something was cut, and the reader's next
     * question is *by what, and how do I undo it* — answering that four hundred pixels away
     * in a 0.9rem chevron is answering it somewhere nobody is looking.
     *
     * Built for every row with prose and **shown only where a clamp bit**, which is a
     * measurement and therefore not a decision this function can make — [revealShowMore]
     * makes it, once the row is in the document. A row whose summary happens to fit in two
     * lines has nothing more to show, and a button that says otherwise is a small lie
     * repeated once per row.
     */
    private fun showMore(id: Long): HTMLElement {
        val more = button("Show more ▾", "lp-row-more") { model.onRowExpandToggled(id) }
        // The row's own click focuses it, which is not what pressing this asks for.
        more.addEventListener("click", { event -> event.stopPropagation() })
        return more
    }

    /**
     * The model's half of a row: a summary, two skeleton bars, a failure, or nothing.
     *
     * One function for all four so that the collapsed and expanded rows cannot disagree
     * about what a waiting bookmark looks like — which is the sort of difference nobody
     * sees until they expand a row to find out why it is blank.
     *
     * @return the block, or null when the model has nothing to say about this bookmark at
     *   all — which is every bookmark on a server with no key, and is why the caller falls
     *   back to the owner's own note rather than drawing an empty slot.
     */
    private fun aiBlock(bookmark: Bookmark, clamp: Boolean): HTMLElement? = when {
        bookmark.isAwaitingSummary -> skeleton()
        bookmark.aiSummary.isNotBlank() -> prose("AI", "lp-row-ai-pill", bookmark.aiSummary, clamp)
        bookmark.aiState == AiStates.FAILED -> failedLine(bookmark)
        else -> null
    }

    /**
     * Two bars where the summary will land.
     *
     * Deliberately not a spinner. A spinner says *this is blocked, wait*; the row is not
     * blocked — its link opens, its tags filter, its note reads — it is merely incomplete,
     * and two bars the width of the sentence that is coming say exactly that. The `AI` pill
     * is absent because there is nothing yet to attribute to anybody.
     *
     * Widths differ so the pair reads as a paragraph rather than as a progress bar drawn
     * twice; they are in the stylesheet, where a width belongs.
     */
    private fun skeleton(): HTMLElement {
        val block = element("div", "lp-row-skeleton")
        block.appendChild(element("span", "lp-row-skeleton-bar lp-row-skeleton-wide"))
        block.appendChild(element("span", "lp-row-skeleton-bar lp-row-skeleton-narrow"))
        return block
    }

    /**
     * One quiet line, the reason on hover, and *Try again*.
     *
     * Quiet on purpose — dim rather than the danger colour — because nothing here is wrong
     * with the *bookmark*: the link is saved, it opens, and it is filed under whatever its
     * owner filed it under. What failed is one optional embellishment, so this is a note on
     * a row and never an error state for it.
     *
     * The sentence is on `title` rather than on screen for the same reason the row shows
     * two lines of a summary and not twenty: *why* a page could not be read matters once,
     * to somebody who has decided to care, and it is the server's own sentence — see
     * `Bookmark.aiError`, which is written by this server and never echoes a provider.
     */
    private fun failedLine(bookmark: Bookmark): HTMLElement {
        val line = element("div", "lp-row-failed")
        line.appendChild(element("span", "lp-row-failed-text", "No summary — the page could not be read."))
        if (bookmark.aiError.isNotBlank()) line.title = bookmark.aiError
        val retry = button("Try again", "lp-row-retry") { model.onTryAgainRequested(bookmark.id) }
        // The row's click handler focuses it; a button inside the row should do its own job
        // and not also move the focus somewhere the reader did not ask for.
        retry.addEventListener("click", { event -> event.stopPropagation() })
        line.appendChild(retry)
        return line
    }

    /** A labelled block of prose: a small pill in its own column, then the words. */
    private fun prose(pill: String, pillClass: String, text: String, clamp: Boolean): HTMLElement {
        val block = element("div", if (clamp) "lp-row-prose lp-row-prose-clamp" else "lp-row-prose")
        block.appendChild(element("span", "lp-row-pill $pillClass", pill))
        block.appendChild(element("span", "lp-row-prose-text", text))
        return block
    }

    private fun expandedActions(bookmark: Bookmark): HTMLElement {
        val actions = element("div", "lp-row-foot")
        // *Show less*, and not *Collapse*, because it is the undo of *Show more* — the same
        // control, named the same way round, in the place the reader's eye ends up.
        actions.appendChild(
            button("Show less ▴", "lp-list-link") { model.onRowExpandToggled(bookmark.id) },
        )
        actions.appendChild(
            button("Edit in a window", "lp-list-link") { onEditBookmark(bookmark.id) },
        )
        actions.appendChild(element("span", "lp-list-spacer"))
        actions.appendChild(
            element(
                "span",
                "lp-row-saved",
                "SAVED ${Date(bookmark.createdAt.toDouble()).toLocaleString().uppercase()} · SPACE TO COLLAPSE",
            ),
        )
        return actions
    }

    /**
     * The title, as an anchor.
     *
     * An anchor so the ordinary things work: middle-click, cmd-click, copy link address and
     * the browser's own visited styling. `noopener` is not optional on a `target=_blank`
     * link to somewhere the user chose — without it the opened page gets a handle on this
     * window through `window.opener` and can navigate it — and `noreferrer` because where
     * somebody keeps their bookmarks is not the visited site's business.
     */
    private fun externalLink(bookmark: Bookmark): HTMLAnchorElement {
        val link = document.createElement("a") as HTMLAnchorElement
        link.className = "lp-row-title"
        link.href = bookmark.url
        link.target = "_blank"
        link.rel = "noopener noreferrer"
        // `textContent`, never `innerHTML`: a title arrives from a page the user visited,
        // which is to say from a stranger. The URL is safe to render for the same reason it
        // is safe to link — the server refuses every scheme but http and https.
        link.textContent = bookmark.title.ifBlank { bookmark.url }
        // For the one title in a thousand that is long enough to be cut anyway. The clamp is
        // four lines, which is a hundred and sixty characters in the narrowest card the
        // layout will build, so what this is here for is the title that is a paragraph —
        // and for those, hovering beats opening the row.
        link.title = link.textContent ?: ""
        return link
    }

    // ── The footer ───────────────────────────────────────────────────────────

    private fun renderFooter(ready: State.Ready) {
        footer.innerHTML = ""
        val counts = buildString {
            append("${ready.shown} SHOWN")
            if (ready.shown != ready.total) append(" OF ${ready.total}")
            if (ready.expandedIds.isNotEmpty()) append(" · ${ready.expandedIds.size} EXPANDED")
        }
        footer.appendChild(element("span", "lp-list-foot-item", counts))
        footer.appendChild(
            element(
                "span",
                "lp-list-foot-item",
                when {
                    ready.isRefreshing -> "REFRESHING…"
                    // A running timer somebody can see. It is the one thing on this window
                    // that is happening without anybody asking, and a window that quietly
                    // polls the server is worth one word about it.
                    ready.isAwaitingSummaries -> "WAITING FOR SUMMARIES…"
                    // ── What replaced "REFRESHED 4 MIN AGO" ──────────────────
                    //
                    // That line answered "how stale is this?", which was a real question while
                    // the only way to find out about a change was to press ↻. With a live
                    // channel it is noise: the answer is always "current", and a number
                    // counting up beside it invites the reader to distrust a list that is in
                    // fact up to date.
                    //
                    // **Disconnected is the one thing about freshness still worth saying**,
                    // because it is the state where what you are looking at can silently be
                    // wrong — and it is the state the old line could never distinguish, since
                    // a stalled client and a quiet one both showed a growing number of
                    // minutes. Silence when connected, a word when not.
                    !isEventStreamConnected() -> "NOT CONNECTED"
                    else -> ""
                },
            ),
        )
        footer.appendChild(element("span", "lp-list-spacer"))
        // Only keys that do something. The canvas also lists ⌘K and T; there is no command
        // palette and no inline tag action, and a legend that lies is worse than none.
        listOf("J / K MOVE", "SPACE EXPAND", "E EDIT").forEach {
            footer.appendChild(element("span", "lp-list-key", it))
        }
    }

    // ── Keys ─────────────────────────────────────────────────────────────────

    /**
     * `j`, `k`, `space`, `e`.
     *
     * Ignored while the caret is in a field, which is not a nicety: `e` is a letter, and a
     * search box that swallowed it into "open the editor" would be unusable. The `#tag`
     * control already stops the keys it consumes (see `TagInputField.consume`), so its
     * `Esc` and `⏎` never arrive here at all.
     */
    private fun onKeyDown(event: KeyboardEvent) {
        if ((event.target as? HTMLElement).isInteractive()) return
        if (event.metaKey || event.ctrlKey || event.altKey) return
        when (event.key) {
            "j" -> model.onFocusMoved(1)
            "k" -> model.onFocusMoved(-1)
            " " -> model.focusedBookmarkId()?.let { model.onRowExpandToggled(it) }
            "e" -> model.focusedBookmarkId()?.let { onEditBookmark(it) }
            else -> return
        }
        // Only for a key this window took. `space` would scroll the body and `j`/`k` would
        // reach whatever else is listening; everything else falls through untouched.
        event.preventDefault()
        event.stopPropagation()
    }

    // ── Small builders ───────────────────────────────────────────────────────

    /** A knob: a mono uppercase label and a `<select>` of values. */
    private fun knob(
        label: String,
        value: String,
        options: List<Pair<String, String>>,
        onPick: (String) -> Unit,
    ): HTMLElement {
        val wrap = element("div", "lp-list-knob")
        wrap.appendChild(element("span", "lp-list-knob-label", label))
        val select = document.createElement("select") as HTMLSelectElement
        select.className = "lp-list-knob-select"
        options.forEach { (key, words) ->
            val option = document.createElement("option") as org.w3c.dom.HTMLOptionElement
            option.value = key
            option.textContent = words
            select.appendChild(option)
        }
        // Written after the options exist, and tolerant of a value from a later client:
        // an unknown one leaves the browser on the first option while the model keeps its
        // own answer, which is exactly the `else` branch every `when` over these has.
        select.value = value
        select.addEventListener("change", { onPick(select.value) })
        wrap.appendChild(select)
        return wrap
    }

    /** A segmented strip: one filled cell, the rest outlined. */
    private fun segmented(
        options: List<Pair<String, String>>,
        value: String,
        onPick: (String) -> Unit,
    ): HTMLElement {
        val strip = element("div", "lp-list-segmented")
        options.forEach { (key, words) ->
            val cell = button(words, if (key == value) "lp-list-seg lp-list-seg-on" else "lp-list-seg") {
                onPick(key)
            }
            cell.setAttribute("aria-pressed", (key == value).toString())
            strip.appendChild(cell)
        }
        return strip
    }

    /** A switch, as a button that says which way it is. */
    private fun toggle(label: String, on: Boolean, onChange: (Boolean) -> Unit): HTMLElement {
        val control = button(label, if (on) "lp-list-toggle lp-list-toggle-on" else "lp-list-toggle") {
            onChange(!on)
        }
        control.setAttribute("aria-pressed", on.toString())
        return control
    }

    /**
     * A text field, built empty.
     *
     * No initial value on purpose: the one field made through here outlives every repaint,
     * so a value written at construction would be a value written once and never again. What
     * it currently says is a paint's business — see [renderRailPanelHead], which writes it
     * and states when it must not.
     */
    private fun textInput(
        placeholder: String,
        className: String,
        onInput: (String) -> Unit,
    ): HTMLInputElement {
        val input = document.createElement("input") as HTMLInputElement
        input.type = "text"
        input.className = className
        input.placeholder = placeholder
        input.addEventListener("input", { onInput(input.value) })
        return input
    }

    /**
     * Book a repaint for the next animation frame, **at most one** however many times this
     * is called before it fires.
     *
     * The coalescing is the whole point rather than a nicety. Both callers fire in bursts —
     * a `ResizeObserver` several times as a pane settles, a scroll far faster than the
     * screen refreshes — and a frame is the right window to collapse them into, since
     * nothing the reader can perceive happens more often than that. Booking on the frame
     * rather than on a timer means the repaint lands before the browser paints, so there is
     * no flash of the old layout.
     *
     * @param fullRender whether the rail and the knobs have to be rebuilt too, which is
     *   true of a resize and false of a scroll. **Sticky within one frame**: a scroll and a
     *   resize arriving together must do the resize's work, not the scroll's, or the rows
     *   would be laid out at a width the knobs still disagree with.
     *
     * Never cancelled on dispose, and that costs nothing: the frame fires once, writes into
     * nodes that are already detached, and both go to the collector — which is the same
     * argument [observeWidth] makes for not disconnecting itself.
     */
    private fun bookFrame(fullRender: Boolean) {
        if (fullRender) pendingFullRender = true
        if (pendingFrame != null) return
        pendingFrame = window.requestAnimationFrame {
            pendingFrame = null
            val full = pendingFullRender
            pendingFullRender = false
            val ready = lastState?.ready
            if (ready != null) {
                if (full) {
                    // The two panels included, because both are `fixed` and neither travels
                    // with the button it hangs off: a pane that has just been resized has
                    // moved that button, and `AnchoredPanel` places itself on open.
                    renderBar(ready)
                    renderTagPanel(ready)
                    renderViewPanel(ready)
                    renderRows(ready)
                } else {
                    paintWindow()
                    // **And settle, which is not optional on this path.** Scrolling is how
                    // the reader reaches rows nobody has measured, so it is the moment their
                    // guessed heights become real ones — and a paint that skipped it left
                    // every card placed at a height it did not have. What that looks like is
                    // a day header drawn over the last card of the group above it.
                    //
                    // Unanchored, because here the reader owns `scrollTop` — see [settle].
                    settle(ready, keepRowAtTop = false)
                }
            }
        }
    }

    /** The width the rows have, for the auto column decision. */
    private fun bodyWidth(): Int = body.clientWidth.takeIf { it > 0 } ?: root.clientWidth

    private companion object {
        /**
         * How far past the viewport rows are built, in each direction.
         *
         * A viewport's worth is the floor rather than the figure: an ordinary scroll then
         * finds its rows already built and only a flung one can outrun the frame, and on a
         * tall window that budget scales with the window. The constant is what a very short
         * pane gets, where a viewport's worth would be a couple of rows.
         */
        const val MIN_OVERSCAN_PX = 600.0

        /** See [settle]. One pass settles it in practice; this is the guard rail. */
        const val SETTLE_PASSES = 3

        /**
         * How long the search field waits after the last character before it filters.
         *
         * See [onSearchTextChanged]. Under a tenth of a second reads as no delay at all —
         * it is shorter than the gap between two characters of ordinary typing, so the
         * results still land while the reader is looking at the field — while being long
         * enough that a word costs one re-derivation rather than one per letter. Well
         * under `FILTER_WRITE_DELAY_MS`, which is what waits before the filter is *stored*:
         * these must not be the same number, because showing results and writing a
         * workspace to the server are not the same kind of eagerness.
         */
        const val QUERY_DELAY_MS = 90

        /**
         * What a card is assumed to be before this window has ever measured one.
         *
         * A number from looking at the thing rather than a computed one, and it only has to
         * be the right order of magnitude: it is replaced by real measurements as soon as a
         * screenful has been painted, and until then its only job is to make the scrollbar
         * roughly the right size.
         */
        const val DEFAULT_COLLAPSED_PX = 132.0
        const val DEFAULT_EXPANDED_PX = 260.0

        /**
         * How wide the tag panel may get.
         *
         * A cloud of chips wants width — the fewer rows it wraps to, the faster it is to
         * scan — but past this it stops being a control hanging off a button and starts being
         * a second window. Narrower panes clamp it further; see [AnchoredPanel.place].
         */
        const val TAG_PANEL_MAX_PX = 640.0

        /** How wide the *View* panel may get. A column of label-and-value rows, no more. */
        const val VIEW_PANEL_MAX_PX = 320.0

        /**
         * The narrowest pane that still gets `TITLE · URL · SUMMARY` in the field.
         *
         * Both of these are the width at which the thing they gate stops being worth the
         * space it takes from the condition, which is what the bar is actually for. Numbers
         * from looking at it in a split pane rather than computed ones — there is nothing to
         * compute, since what fits depends on the reader's font size too, and being a little
         * conservative costs a reminder rather than a control.
         */
        const val HINT_MIN_PX = 620

        /** The narrowest pane that still gets `N SHOWN`. The footer says it too. */
        const val COUNT_MIN_PX = 430

        /**
         * How far a measurement may be from the assumption before it is worth laying out
         * again. Sub-pixel differences are the browser's rounding, not a card that moved.
         */
        const val TOLERANCE_PX = 0.5

        /**
         * Fold a fresh measurement into a running guess.
         *
         * Weighted towards what is already there, so one unusually tall card does not
         * rewrite where the rest of the collection is assumed to be — the guess is only
         * used for rows nobody has reached yet, and being steadily a little wrong is worth
         * more there than being occasionally very wrong.
         */
        fun blend(guess: Double, measured: Double): Double = (guess * 3 + measured) / 4
    }
}

/**
 * Call [onWidth] whenever [element] changes width.
 *
 * `ResizeObserver` reached dynamically because Kotlin/JS has no external declaration for
 * it. Never disconnected, and that is not a leak worth machinery: the observer holds one
 * element, the element belongs to a window body, and `WindowBodies.dispose` drops the whole
 * body when the window closes — at which point the observer is the only thing referring to
 * a detached node and the collector takes both.
 */
private fun observeWidth(element: HTMLElement, onWidth: (Int) -> Unit) {
    val callback: (dynamic) -> Unit = { onWidth(element.clientWidth) }
    js("new ResizeObserver(callback)").observe(element)
}

/**
 * Whether a keystroke or a click landing here belongs to something else.
 *
 * Fields, buttons, links and selects all take their own input, and a window-level `j` that
 * fired while somebody was typing a `j` into the search box would be a window that cannot
 * be searched.
 */
private fun HTMLElement?.isInteractive(): Boolean {
    val node = this ?: return false
    if (node.isContentEditable) return true
    return node.tagName.lowercase() in setOf("input", "textarea", "select", "button", "a", "option")
}

/** `example.com` from a URL, without the scheme or a leading `www.`. */
internal fun domainOf(url: String): String =
    url.substringAfter("://").substringBefore('/').substringBefore(':').removePrefix("www.")

/** `08:12`, in the reader's own zone. */
private fun timeWords(millis: Long): String {
    val date = Date(millis.toDouble())
    return "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
}

/**
 * A day header's date, as the reader's own locale writes one.
 *
 * `Date` rather than a formatting library: the browser already knows what the reader's zone
 * is and how they write a date, and a formatter in the bundle would be a second opinion
 * about both. Reached dynamically because Kotlin's `Date` external has no overload taking
 * an options object without a locale — and `undefined` for the locale is exactly the
 * request being made, which is "whatever this reader's is".
 *
 * The weekday is worth its width in a list grouped by day: "was that the Tuesday or the
 * Wednesday" is the question a header is being read to answer.
 */
private fun dayHeaderDate(millis: Long): String =
    Date(millis.toDouble()).asDynamic().toLocaleDateString(
        undefined,
        js("({weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'})"),
    ) as String
